import React, { useState, useEffect } from 'react';
import { 
  Lock, MessageSquare, Users, Star, ChevronRight, Plus,
  Heart, Eye, Pin, Send, ArrowLeft, Shield, Crown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PrivateForums = () => {
  const { user, token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    if (token) {
      fetchMembership();
      fetchCategories();
    }
  }, [token]);

  useEffect(() => {
    if (selectedCategory && token) {
      fetchPosts();
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedPost && token) {
      fetchReplies();
    }
  }, [selectedPost]);

  const fetchMembership = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/membership/my-membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembership(response.data);
    } catch (error) {
      console.error('Error fetching membership:', error);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/private-forums/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not premium
        setCategories([]);
      } else {
        console.error('Error fetching categories:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-forums/posts?category_id=${selectedCategory.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchReplies = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-forums/posts/${selectedPost.id}/replies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplies(response.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
    }
  };

  const handleCreatePost = async (title, content, tags) => {
    try {
      await axios.post(
        `${API_URL}/api/private-forums/posts`,
        {
          category_id: selectedCategory.id,
          title,
          content,
          tags
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post created successfully');
      setNewPostDialog(false);
      fetchPosts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleCreateReply = async () => {
    if (!replyContent.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/api/private-forums/posts/${selectedPost.id}/replies`,
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply posted');
      setReplyContent('');
      fetchReplies();
    } catch (error) {
      toast.error('Failed to post reply');
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/private-forums/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.action === 'liked' ? 'Liked!' : 'Unliked');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const getGroupColor = (groupId) => {
    const colors = {
      all: 'bg-blue-100 text-blue-800',
      breeder: 'bg-purple-100 text-purple-800',
      trainer: 'bg-green-100 text-green-800',
      veterinarian: 'bg-red-100 text-red-800',
      groomer: 'bg-pink-100 text-pink-800',
      rescue: 'bg-orange-100 text-orange-800',
      kennel_club: 'bg-yellow-100 text-yellow-800',
      pet_sitter: 'bg-cyan-100 text-cyan-800',
      vendor: 'bg-indigo-100 text-indigo-800'
    };
    return colors[groupId] || colors.all;
  };

  // Non-premium view
  if (!loading && (!membership || membership.tier !== 'premium')) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <Lock className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Premium Members Only</h2>
              <p className="text-muted-foreground mb-6">
                The Private Professional Forums are exclusive to Premium members. 
                Upgrade to connect with other professionals in your field.
              </p>
              <div className="bg-muted/50 rounded-lg p-6 mb-6 text-left">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Premium Benefits
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-500" />
                    Access to private professional forums
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    Connect with breeders, trainers, vets & more
                  </li>
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    Professional badge on your profile
                  </li>
                  <li className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-purple-500" />
                    Group-specific discussion categories
                  </li>
                </ul>
              </div>
              <Button size="lg">
                Upgrade to Premium - $14.99/month
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Post view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedPost(null)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {selectedCategory?.name}
          </Button>

          {/* Post */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getGroupColor(selectedPost.group_id)}>
                      {selectedPost.category_name}
                    </Badge>
                    {selectedPost.is_pinned && (
                      <Badge variant="outline">
                        <Pin className="w-3 h-3 mr-1" /> Pinned
                      </Badge>
                    )}
                  </div>
                  <CardTitle>{selectedPost.title}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap mb-4">{selectedPost.content}</p>
              
              {selectedPost.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {selectedPost.tags.map((tag, i) => (
                    <Badge key={i} variant="secondary">#{tag}</Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {(selectedPost.author_display_name || selectedPost.author_username)[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium flex items-center gap-1">
                      {selectedPost.author_display_name || selectedPost.author_username}
                      {selectedPost.is_verified_professional && (
                        <Shield className="w-4 h-4 text-blue-500" />
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(selectedPost.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleLikePost(selectedPost.id)}
                >
                  <Heart className="w-4 h-4 mr-1" />
                  {selectedPost.likes_count}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Replies */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold">{replies.length} Replies</h3>
            {replies.map(reply => (
              <Card key={reply.id}>
                <CardContent className="p-4">
                  <p className="mb-3">{reply.content}</p>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-xs">
                        {(reply.author_display_name || reply.author_username)[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm font-medium flex items-center gap-1">
                        {reply.author_display_name || reply.author_username}
                        {reply.is_verified_professional && (
                          <Shield className="w-3 h-3 text-blue-500" />
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Reply Form */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  rows={3}
                />
                <Button onClick={handleCreateReply} disabled={!replyContent.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Category view with posts
  if (selectedCategory) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>

          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{selectedCategory.name}</h1>
                <Badge className={getGroupColor(selectedCategory.group_id)}>
                  {selectedCategory.group_name}
                </Badge>
              </div>
              <p className="text-muted-foreground">{selectedCategory.description}</p>
            </div>
            <Button onClick={() => setNewPostDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>

          {posts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Posts Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Be the first to start a discussion in this category!
                </p>
                <Button onClick={() => setNewPostDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map(post => (
                <Card 
                  key={post.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedPost(post)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {post.is_pinned && (
                            <Pin className="w-4 h-4 text-primary" />
                          )}
                          <h3 className="font-semibold hover:text-primary">
                            {post.title}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {post.content}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Avatar className="w-5 h-5">
                              <AvatarFallback className="text-xs">
                                {(post.author_display_name || post.author_username)[0].toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            {post.author_display_name || post.author_username}
                            {post.is_verified_professional && (
                              <Shield className="w-3 h-3 text-blue-500" />
                            )}
                          </span>
                          <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {post.replies_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {post.views_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {post.likes_count}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* New Post Dialog */}
          <NewPostDialog
            open={newPostDialog}
            onOpenChange={setNewPostDialog}
            category={selectedCategory}
            onSubmit={handleCreatePost}
          />
        </div>
      </div>
    );
  }

  // Categories view
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
            <Lock className="w-8 h-8 text-primary" />
            Professional Forums
          </h1>
          <p className="text-muted-foreground mt-1">
            Private discussions for premium professional members
          </p>
        </div>

        {/* Your Groups */}
        {membership?.professional_groups?.length > 0 && (
          <Card className="mb-6 bg-primary/5">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-2">Your Professional Groups:</p>
              <div className="flex flex-wrap gap-2">
                {membership.professional_groups.map(group => (
                  <Badge key={group} className={getGroupColor(group)}>
                    {group.charAt(0).toUpperCase() + group.slice(1).replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(category => (
            <Card 
              key={category.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedCategory(category)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge className={`${getGroupColor(category.group_id)} mb-2`}>
                      {category.group_name}
                    </Badge>
                    <h3 className="font-semibold">{category.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {category.description}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground pt-3 border-t">
                  <span>{category.post_count} posts</span>
                  {category.last_post_at && (
                    <span>Last: {formatDistanceToNow(new Date(category.last_post_at), { addSuffix: true })}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// New Post Dialog
const NewPostDialog = ({ open, onOpenChange, category, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setLoading(true);
    await onSubmit(
      title,
      content,
      tags ? tags.split(',').map(t => t.trim()).filter(t => t) : []
    );
    setLoading(false);
    setTitle('');
    setContent('');
    setTags('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Post in {category?.name}</DialogTitle>
          <DialogDescription>
            Share your thoughts with fellow professionals
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              maxLength={300}
            />
          </div>

          <div className="space-y-2">
            <Label>Content *</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your post..."
              rows={6}
              maxLength={10000}
            />
          </div>

          <div className="space-y-2">
            <Label>Tags (comma-separated)</Label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="breeding, genetics, health"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Posting...' : 'Post'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PrivateForums;
